(() => {
  const API_KEY = '__JB_REAL_TRIPS__';

  const createApi = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const smoothScrollTo = (element, requestedLeft) => {
      if (!element) return;
      const maximum = Math.max(0, element.scrollWidth - element.clientWidth);
      const target = Math.max(0, Math.min(maximum, requestedLeft));
      if (Math.abs(target - element.scrollLeft) < 1) return;
      element.scrollTo({ left: target, behavior: reduce ? 'auto' : 'smooth' });
    };

    const glide = rail => {
      if (!rail || rail.dataset.jbGlide === 'true') return;
      rail.dataset.jbGlide = 'true';

      let dragging = false;
      let moved = false;
      let startX = 0;
      let startScroll = 0;
      let lastX = 0;
      let lastTime = 0;
      let velocity = 0;
      let frame = 0;
      let pending = 0;

      rail.addEventListener('dragstart', event => event.preventDefault());

      rail.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        if (event.target.closest('input,select,textarea')) return;
        if (rail.scrollWidth <= rail.clientWidth + 1) return;

        dragging = true;
        moved = false;
        startX = lastX = event.clientX;
        lastTime = performance.now();
        startScroll = pending = rail.scrollLeft;
        velocity = 0;
      });

      rail.addEventListener('pointermove', event => {
        if (!dragging) return;
        const delta = event.clientX - startX;

        if (!moved && Math.abs(delta) > 5) {
          moved = true;
          rail.setPointerCapture?.(event.pointerId);
          rail.classList.add('is-dragging');
        }

        if (!moved) return;

        const now = performance.now();
        const elapsed = Math.max(1, now - lastTime);
        velocity = velocity * 0.6 + ((lastX - event.clientX) / elapsed) * 0.4;
        lastX = event.clientX;
        lastTime = now;
        pending = startScroll - delta;

        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          rail.scrollLeft = pending;
        });
      });

      const release = () => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;

        cancelAnimationFrame(frame);
        rail.scrollLeft = pending;

        const fresh = performance.now() - lastTime < 90;
        const throwDistance = fresh
          ? Math.max(-rail.clientWidth, Math.min(rail.clientWidth, velocity * 260))
          : 0;

        rail.classList.remove('is-dragging');
        requestAnimationFrame(() => smoothScrollTo(rail, rail.scrollLeft + throwDistance));
        window.setTimeout(() => {
          moved = false;
        }, 0);
      };

      rail.addEventListener('pointerup', release);
      rail.addEventListener('pointercancel', release);
      rail.addEventListener('lostpointercapture', release);
      rail.addEventListener('click', event => {
        if (!moved && !rail.classList.contains('is-dragging')) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);
    };

    const previewVideos = component => {
      component.querySelectorAll('.jb-real-trips__preview-video').forEach(video => {
        if (video.dataset.jbPreviewReady === 'true') return;
        video.dataset.jbPreviewReady = 'true';
        video.muted = true;
        video.defaultMuted = true;
        video.loop = true;
        video.playsInline = true;
        video.autoplay = true;

        const play = () => video.play().catch(() => {});
        const pause = () => {
          if (!video.paused) video.pause();
        };

        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.15 && !document.hidden) play();
            else pause();
          }, { threshold: [0, 0.15, 0.5] });
          observer.observe(video);
        } else {
          play();
        }

        document.addEventListener('visibilitychange', () => {
          if (document.hidden) pause();
          else play();
        });
      });
    };

    const initRails = () => {
      document.querySelectorAll('[data-jb-real-trips]').forEach(component => {
        if (component.dataset.jbReady === 'true') {
          previewVideos(component);
          return;
        }

        component.dataset.jbReady = 'true';
        const track = component.querySelector('[data-jb-action-track]');
        if (!track) return;

        glide(track);
        previewVideos(component);

        const step = () => {
          const item = track.querySelector('figure');
          if (!item) return Math.max(120, track.clientWidth * 0.35);
          const styles = getComputedStyle(track);
          const gap = parseFloat(styles.columnGap || styles.gap) || 10;
          return item.getBoundingClientRect().width + gap;
        };

        component.querySelector('[data-jb-action-prev]')?.addEventListener('click', () => {
          smoothScrollTo(track, track.scrollLeft - step());
        });

        component.querySelector('[data-jb-action-next]')?.addEventListener('click', () => {
          smoothScrollTo(track, track.scrollLeft + step());
        });
      });
    };

    const reelManager = (() => {
      const imageDuration = 5000;
      let reel;
      let items = [];
      let index = 0;
      let paused = false;
      let muted = true;
      let startedAt = 0;
      let elapsedBefore = 0;
      let frame = 0;
      let wheelLock = 0;
      let opener = null;

      const icon = {
        close: '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2 2 12" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
        up: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 10 4-4 4 4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        down: '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>',
        play: '<svg class="jb-reel__icon-play" viewBox="0 0 10 14" aria-hidden="true"><path d="M9.6 6.2 1.5.2C.9-.2 0 .2 0 1v12c0 .8.9 1.2 1.5.8l8.1-6c.5-.4.5-1.2 0-1.6Z" fill="currentColor"/></svg><svg class="jb-reel__icon-pause" viewBox="0 0 10 14" aria-hidden="true"><rect width="2.4" height="14" rx="1.2" fill="currentColor"/><rect x="7.6" width="2.4" height="14" rx="1.2" fill="currentColor"/></svg>',
        sound: '<svg class="jb-reel__icon-sound" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6h2.6L8 3v10L4.6 10H2Z" fill="currentColor"/><path d="M10.5 5.5a3.5 3.5 0 0 1 0 5M12.5 3.5a6.3 6.3 0 0 1 0 9" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg><svg class="jb-reel__icon-muted" viewBox="0 0 16 16" aria-hidden="true"><path d="M2 6h2.6L8 3v10L4.6 10H2Z" fill="currentColor"/><path d="m10.5 6 4 4m0-4-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
      };

      const escapeAttribute = value => String(value || '')
        .replaceAll('&', '&amp;')
        .replaceAll('"', '&quot;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;');

      const slides = () => reel ? [...reel.querySelectorAll('.jb-reel__slide')] : [];
      const activeVideo = () => slides()[index]?.querySelector('video');

      const build = () => {
        if (reel) return;

        reel = document.createElement('div');
        reel.className = 'jb-reel';
        reel.hidden = true;
        reel.innerHTML = `
          <div class="jb-reel__backdrop" data-jb-reel-close></div>
          <section class="jb-reel__panel" role="dialog" aria-modal="false" aria-label="Real trips" tabindex="-1">
            <div class="jb-reel__bars" aria-hidden="true"></div>
            <div class="jb-reel__grip" data-jb-reel-drag title="Drag to move"><span></span></div>
            ${['n','s','e','w','ne','nw','se','sw'].map(direction => `<span class="jb-reel__resize jb-reel__resize--${direction}" data-jb-reel-resize="${direction}" aria-hidden="true"></span>`).join('')}
            <button type="button" class="jb-reel__close" data-jb-reel-close aria-label="Close">${icon.close}</button>
            <div class="jb-reel__viewport"><div class="jb-reel__track"></div><span class="jb-reel__spinner" aria-hidden="true"></span></div>
            <button type="button" class="jb-reel__toggle" aria-label="Pause">${icon.play}</button>
            <div class="jb-reel__nav">
              <button type="button" class="jb-reel__arrow" data-jb-reel-prev aria-label="Previous">${icon.up}</button>
              <button type="button" class="jb-reel__arrow" data-jb-reel-next aria-label="Next">${icon.down}</button>
            </div>
            <button type="button" class="jb-reel__mute" aria-label="Unmute" hidden>${icon.sound}</button>
            <p class="jb-reel__count" aria-live="polite"></p>
          </section>`;

        document.body.appendChild(reel);
        const panel = reel.querySelector('.jb-reel__panel');

        reel.addEventListener('click', event => {
          if (event.target.closest('[data-jb-reel-close]')) {
            close();
            return;
          }
          if (event.target.closest('[data-jb-reel-prev]')) {
            go(index - 1);
            return;
          }
          if (event.target.closest('[data-jb-reel-next]')) {
            go(index + 1);
            return;
          }
          if (event.target.closest('.jb-reel__mute')) {
            setMuted(!muted);
            return;
          }
          if (event.target.closest('.jb-reel__toggle') || event.target.closest('.jb-reel__viewport')) {
            if (reel.dataset.swiped === 'true') return;
            setPaused(!paused, true);
          }
        });

        const margin = 8;
        const minWidth = 220;
        const minHeight = 340;

        const clampRect = rect => {
          const maxWidth = Math.max(160, window.innerWidth - margin * 2);
          const maxHeight = Math.max(240, window.innerHeight - margin * 2);
          const width = Math.max(Math.min(minWidth, maxWidth), Math.min(maxWidth, rect.width));
          const height = Math.max(Math.min(minHeight, maxHeight), Math.min(maxHeight, rect.height));
          const left = Math.max(margin, Math.min(window.innerWidth - width - margin, rect.left));
          const top = Math.max(margin, Math.min(window.innerHeight - height - margin, rect.top));
          return { left, top, width, height };
        };

        const placePanel = rect => {
          const next = clampRect(rect);
          Object.assign(panel.style, {
            left: `${next.left}px`,
            top: `${next.top}px`,
            width: `${next.width}px`,
            height: `${next.height}px`,
            right: 'auto',
            bottom: 'auto'
          });
          reel.classList.add('is-placed');
        };

        let gesture;

        panel.addEventListener('pointerdown', event => {
          const dragHandle = event.target.closest('[data-jb-reel-drag]');
          const resizeHandle = event.target.closest('[data-jb-reel-resize]');
          if (!dragHandle && !resizeHandle) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;

          event.preventDefault();
          event.stopPropagation();

          const rect = panel.getBoundingClientRect();
          gesture = {
            id: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            rect,
            dir: resizeHandle ? resizeHandle.dataset.jbReelResize : ''
          };

          panel.setPointerCapture?.(event.pointerId);
          reel.classList.add(gesture.dir ? 'is-resizing' : 'is-moving');
        });

        panel.addEventListener('pointermove', event => {
          if (!gesture || gesture.id !== event.pointerId) return;

          const dx = event.clientX - gesture.x;
          const dy = event.clientY - gesture.y;
          const start = gesture.rect;

          if (!gesture.dir) {
            placePanel({ left: start.left + dx, top: start.top + dy, width: start.width, height: start.height });
            return;
          }

          let left = start.left;
          let top = start.top;
          let width = start.width;
          let height = start.height;

          if (gesture.dir.includes('e')) width = start.width + dx;
          if (gesture.dir.includes('s')) height = start.height + dy;
          if (gesture.dir.includes('w')) {
            width = Math.max(minWidth, start.width - dx);
            left = start.right - width;
          }
          if (gesture.dir.includes('n')) {
            height = Math.max(minHeight, start.height - dy);
            top = start.bottom - height;
          }

          placePanel({ left, top, width: Math.max(minWidth, width), height: Math.max(minHeight, height) });
        });

        const endGesture = event => {
          if (!gesture || gesture.id !== event.pointerId) return;
          gesture = undefined;
          reel.classList.remove('is-resizing', 'is-moving');
        };

        panel.addEventListener('pointerup', endGesture);
        panel.addEventListener('pointercancel', endGesture);

        window.addEventListener('resize', () => {
          if (!reel.hidden && reel.classList.contains('is-placed')) {
            placePanel(panel.getBoundingClientRect());
          }
        }, { passive: true });

        panel.addEventListener('wheel', event => {
          if (Math.abs(event.deltaY) < 12) return;
          event.preventDefault();
          const now = performance.now();
          if (now - wheelLock < 650) return;
          wheelLock = now;
          go(index + (event.deltaY > 0 ? 1 : -1));
        }, { passive: false });

        const viewport = reel.querySelector('.jb-reel__viewport');
        let swipe;

        viewport.addEventListener('pointerdown', event => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          swipe = {
            id: event.pointerId,
            y: event.clientY,
            x: event.clientX,
            time: performance.now()
          };
          reel.dataset.swiped = 'false';
        });

        viewport.addEventListener('pointerup', event => {
          if (!swipe || swipe.id !== event.pointerId) return;
          const deltaY = event.clientY - swipe.y;
          const deltaX = event.clientX - swipe.x;
          const fast = performance.now() - swipe.time < 450;
          swipe = undefined;

          if ((Math.abs(deltaY) > 40 && Math.abs(deltaY) > Math.abs(deltaX)) || (fast && Math.abs(deltaY) > 24)) {
            reel.dataset.swiped = 'true';
            go(index + (deltaY < 0 ? 1 : -1));
            window.setTimeout(() => {
              reel.dataset.swiped = 'false';
            }, 0);
          }
        });

        viewport.addEventListener('pointercancel', () => {
          swipe = undefined;
        });

        document.addEventListener('keydown', event => {
          if (!reel || reel.hidden) return;

          if (event.key === 'Escape') {
            event.preventDefault();
            close();
          } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
            event.preventDefault();
            go(index + 1);
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
            event.preventDefault();
            go(index - 1);
          } else if (event.key === ' ' && !event.target.closest('button')) {
            event.preventDefault();
            setPaused(!paused, true);
          } else if (event.key === 'Tab') {
            const focusable = [...panel.querySelectorAll('button:not([hidden])')];
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
              event.preventDefault();
              last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
        });

        document.addEventListener('visibilitychange', () => {
          if (!reel.hidden && document.hidden) setPaused(true);
        });
      };

      const render = () => {
        const track = reel.querySelector('.jb-reel__track');
        track.innerHTML = items.map((item, itemIndex) => {
          const poster = escapeAttribute(item.image);
          const video = escapeAttribute(item.video);
          const alt = escapeAttribute(item.alt);
          return `
            <div class="jb-reel__slide" data-index="${itemIndex}" aria-hidden="true">
              ${video
                ? `<video src="${video}" poster="${poster}" playsinline muted preload="metadata"></video>`
                : `<img src="${poster}" alt="${alt}" decoding="async" draggable="false">`}
            </div>`;
        }).join('');

        reel.querySelector('.jb-reel__bars').innerHTML = items.map(() => '<span><i></i></span>').join('');
        reel.querySelector('.jb-reel__mute').hidden = !items.some(item => item.video);

        slides().forEach(slide => {
          const media = slide.querySelector('img,video');
          if (!media) {
            slide.classList.add('is-loaded');
            return;
          }

          const done = () => slide.classList.add('is-loaded');

          if (media.tagName === 'IMG') {
            if (media.complete && media.naturalWidth) done();
            else {
              media.addEventListener('load', done, { once: true });
              media.addEventListener('error', done, { once: true });
            }
          } else {
            media.addEventListener('loadeddata', done, { once: true });
            media.addEventListener('error', done, { once: true });
            media.addEventListener('playing', done);
            media.addEventListener('waiting', () => slide.classList.remove('is-loaded'));
            media.addEventListener('ended', () => {
              if (slides()[index] === slide) go(index + 1);
            });
          }
        });
      };

      const updateBars = progress => {
        reel.querySelectorAll('.jb-reel__bars i').forEach((bar, barIndex) => {
          const value = barIndex < index ? 1 : barIndex > index ? 0 : progress;
          bar.style.transform = `scaleX(${value})`;
        });
      };

      const tick = () => {
        cancelAnimationFrame(frame);
        if (!reel || reel.hidden) return;

        const video = activeVideo();
        let progress = 0;

        if (video) {
          progress = video.duration ? video.currentTime / video.duration : 0;
        } else {
          const slide = slides()[index];
          const ready = slide?.classList.contains('is-loaded');
          if (!ready) startedAt = performance.now();
          const elapsed = elapsedBefore + (paused || !ready ? 0 : performance.now() - startedAt);
          progress = Math.min(1, elapsed / imageDuration);
          if (progress >= 1 && !paused) {
            go(index + 1);
            return;
          }
        }

        updateBars(progress);
        frame = requestAnimationFrame(tick);
      };

      const setMuted = value => {
        muted = value;
        const button = reel.querySelector('.jb-reel__mute');
        button.classList.toggle('is-muted', muted);
        button.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
        const video = activeVideo();
        if (video) video.muted = muted;
      };

      const flashToggle = () => {
        const toggle = reel.querySelector('.jb-reel__toggle');
        toggle.classList.remove('is-flash');
        void toggle.offsetWidth;
        toggle.classList.add('is-flash');
      };

      const setPaused = (value, flash = false) => {
        const video = activeVideo();
        if (value && !paused) elapsedBefore += performance.now() - startedAt;
        if (!value && paused) startedAt = performance.now();

        paused = value;
        reel.classList.toggle('is-paused', paused);

        const toggle = reel.querySelector('.jb-reel__toggle');
        toggle.setAttribute('aria-label', paused ? 'Play' : 'Pause');

        if (video) {
          if (paused) video.pause();
          else video.play().catch(() => {});
        }

        if (flash) flashToggle();
        tick();
      };

      const go = (target, immediate = false) => {
        if (!items.length) return;

        const count = items.length;
        const next = (target + count) % count;
        const jump = Math.abs(next - index) > 1 && !immediate;
        const track = reel.querySelector('.jb-reel__track');

        slides().forEach((slide, slideIndex) => {
          const video = slide.querySelector('video');
          if (video && slideIndex !== next) {
            video.pause();
            try {
              video.currentTime = 0;
            } catch (_) {}
          }
        });

        index = next;
        reel.classList.toggle('is-jumping', jump || immediate);
        track.style.transform = `translate3d(0,${-index * 100}%,0)`;
        void track.offsetWidth;

        window.clearTimeout(reel.jbJumpTimer);
        reel.jbJumpTimer = window.setTimeout(() => {
          reel.classList.remove('is-jumping');
        }, 360);

        slides().forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });

        [index + 1, index - 1].forEach(position => {
          const neighbour = slides()[(position + count) % count]?.querySelector('video');
          if (neighbour) neighbour.preload = 'auto';
        });

        reel.querySelector('.jb-reel__count').textContent = `${index + 1} / ${count}`;
        elapsedBefore = 0;
        startedAt = performance.now();

        const video = activeVideo();
        if (video) {
          video.muted = muted;
          if (!paused) video.play().catch(() => {});
        }

        tick();
      };

      const open = (rail, startIndex, trigger) => {
        build();

        items = [...rail.querySelectorAll('[data-jb-reel-item]')]
          .map(button => ({
            image: button.dataset.jbReelImage || '',
            video: button.dataset.jbReelVideo || '',
            alt: button.querySelector('img')?.alt || button.getAttribute('aria-label') || ''
          }))
          .filter(item => item.image || item.video);

        if (!items.length) return;

        opener = trigger;
        render();
        reel.querySelector('.jb-reel__panel').setAttribute('aria-label', rail.dataset.jbReelTitle || 'Real trips');
        paused = false;
        reel.classList.remove('is-paused');
        reel.hidden = false;
        index = Math.min(Math.max(0, startIndex), items.length - 1);
        go(index, true);
        setMuted(muted);
        requestAnimationFrame(() => reel.classList.add('is-open'));
        reel.querySelector('.jb-reel__panel').focus({ preventScroll: true });
      };

      const close = () => {
        if (!reel || reel.hidden) return;

        cancelAnimationFrame(frame);
        slides().forEach(slide => slide.querySelector('video')?.pause());
        reel.classList.remove('is-open');

        window.setTimeout(() => {
          if (reel.classList.contains('is-open')) return;
          reel.hidden = true;
          reel.querySelector('.jb-reel__track').innerHTML = '';
        }, reduce ? 0 : 260);

        opener?.focus?.({ preventScroll: true });
      };

      const bindRails = () => {
        document.querySelectorAll('[data-jb-reel-rail]').forEach(rail => {
          if (rail.dataset.jbReelReady === 'true') return;
          rail.dataset.jbReelReady = 'true';

          rail.addEventListener('click', event => {
            const trigger = event.target.closest('[data-jb-reel-item]');
            if (!trigger || !rail.contains(trigger)) return;

            event.preventDefault();
            const list = [...rail.querySelectorAll('[data-jb-reel-item]')];
            open(rail, Math.max(0, list.indexOf(trigger)), trigger);
          });
        });
      };

      return { bindRails };
    })();

    const init = () => {
      initRails();
      reelManager.bindRails();
    };

    return { init };
  };

  if (!window[API_KEY]) {
    window[API_KEY] = createApi();
  }

  const init = () => window[API_KEY].init();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
